import { z } from 'zod';

// RUF-360: per-field confidence + source, keyed by the sku_oem_spec column
// name it describes (e.g. "ga_date", "tdp_watts"). Populated by sources that
// publish their own per-field confidence rating (currently: NDX).
export const fieldProvenanceSchema = z.record(
  z.object({
    confidence: z.string(),
    source: z.string(),
  }),
);
export type FieldProvenance = z.infer<typeof fieldProvenanceSchema>;

// SIL-1621: system cooling class. Mirrors sku_oem_spec_cooling_check
// (migration 1019). NULL = unknown, never defaulted to 'air'.
export const COOLING_CLASSES = ['air', 'liquid_assisted', 'direct_liquid'] as const;
export type CoolingClass = (typeof COOLING_CLASSES)[number];

/**
 * Map free cooling text ("Direct-to-Chip Liquid Cooled", "Air-cooled with
 * liquid assistance", "DLC", an enum value) onto a CoolingClass, else null.
 * Order matters: explicit direct-liquid, then liquid-assisted, then any other
 * "liquid-cooled" (a liquid-cooled server/rack is cold-plate DLC), then air -
 * so "Air-cooled with liquid assistance" is liquid_assisted, not air. Keep in
 * step with SQL cooling_class_from_text() (migration 1020).
 */
export function normalizeCooling(raw: unknown): CoolingClass | null {
  if (typeof raw !== 'string') return null;
  const s = raw.toLowerCase();
  if (/direct[_\s-]*(to[\s-]*chip|liquid)|\bdlc\b|100%\s*liquid|fully\s+liquid/.test(s)) return 'direct_liquid';
  if (/liquid[_\s-]*assist/.test(s)) return 'liquid_assisted';
  if (/liquid[\s-]*cooled/.test(s)) return 'direct_liquid';
  if (/^air$|air[\s-]*cooled/.test(s)) return 'air';
  return null;
}

export const oemSpecPayloadSchema = z.object({
  sku_id: z.string().uuid(),
  scrape_source_id: z.string().uuid().nullable().optional(),
  import_batch_id: z.string().uuid().nullable().optional(),
  // GPU fields
  vram_gb: z.number().positive().nullable().optional(),
  tdp_watts: z.number().int().positive().nullable().optional(),
  fp16_tflops: z.number().positive().nullable().optional(),
  memory_bandwidth_gbps: z.number().positive().nullable().optional(),
  interconnect_type: z.string().nullable().optional(),
  architecture: z.string().nullable().optional(),
  memory_type: z.string().nullable().optional(),
  // Server fields
  form_factor: z.string().nullable().optional(),
  gpu_slots: z.number().int().min(0).nullable().optional(),
  psu_redundancy: z.string().nullable().optional(),
  max_gpu_count: z.number().int().positive().nullable().optional(),
  cpu_socket_count: z.number().int().positive().nullable().optional(),
  pcie_gen: z.number().int().nullable().optional(),
  max_ram_gb: z.number().int().positive().nullable().optional(),
  power_supply_watts: z.number().int().positive().nullable().optional(),
  // SIL-393: DIMM slot count. Paired with max_ram_gb to derive max DIMM size
  // at read time (max_ram_gb / dimm_slots) — max DIMM size itself is never stored.
  dimm_slots: z.number().int().positive().nullable().optional(),
  // SIL-1621: any free text is normalized; unknown -> null, so an off-vocabulary
  // LLM answer can neither fail validation nor trip the DB CHECK.
  cooling: z.preprocess(normalizeCooling, z.enum(COOLING_CLASSES).nullable()).optional(),
  // RUF-360: NDX enrichment fields (lifecycle / thermal / operational)
  ga_date: z.string().nullable().optional(),
  typical_power_draw_watts: z.number().int().positive().nullable().optional(),
  max_power_draw_watts: z.number().int().positive().nullable().optional(),
  // Cloud VM fields
  equivalent_server_models: z.array(z.string()).nullable().optional(),
  cpu_model: z.string().nullable().optional(),
  gpu_model: z.string().nullable().optional(),
  market_search_term: z.string().nullable().optional(),
  // Provenance — the actual page the specs were retrieved from + its source name
  source_url: z.string().nullable().optional(),
  source_vendor: z.string().nullable().optional(),
  // RUF-360: per-field confidence gate metadata (see fieldProvenanceSchema above)
  field_provenance: fieldProvenanceSchema.nullable().optional(),
  // Audit
  raw_payload: z.record(z.unknown()).nullable().optional(),
  scraped_at: z.string().datetime().optional(),
});
export type OemSpecPayload = z.infer<typeof oemSpecPayloadSchema>;

export const oemPricePayloadSchema = z.object({
  sku_id: z.string().uuid(),
  server_gpu_config_id: z.string().uuid().nullable().optional(),
  scrape_source_id: z.string().uuid().nullable().optional(),
  import_batch_id: z.string().uuid().nullable().optional(),
  vendor: z.string().min(1),
  list_price_usd: z.number().positive(),
  currency: z.string().length(3).default('USD'),
  notes: z.string().nullable().optional(),
});
export type OemPricePayload = z.infer<typeof oemPricePayloadSchema>;

export const scrapeRunMetricPayloadSchema = z.object({
  import_batch_id: z.string().uuid(),
  vendor: z.string().min(1),
  source_type: z.string().min(1),
  urls_attempted: z.number().int().min(0).default(0),
  urls_succeeded: z.number().int().min(0).default(0),
  items_fetched: z.number().int().min(0).default(0),
  items_parsed: z.number().int().min(0).default(0),
  items_failed: z.number().int().min(0).default(0),
  llm_calls_made: z.number().int().min(0).default(0),
  duration_ms: z.number().int().nullable().optional(),
  error_summary: z.array(z.object({
    url: z.string(),
    code: z.string(),
    message: z.string(),
  })).nullable().optional(),
  started_at: z.string().datetime(),
  completed_at: z.string().datetime().nullable().optional(),
});
export type ScrapeRunMetricPayload = z.infer<typeof scrapeRunMetricPayloadSchema>;
